/**
 * excelExport.ts — Multi-sheet Excel + CSV export utilities.
 * Uses SheetJS (xlsx) for .xlsx generation.
 */
import * as XLSX from 'xlsx';

export interface SheetDefinition {
  name: string;
  headers: string[];
  rows: (string | number | boolean | null | undefined)[][];
}

/**
 * Generate and download a multi-sheet .xlsx workbook.
 */
export function downloadMultiSheetExcel(
  sheets: SheetDefinition[],
  fileName: string
): void {
  const wb = XLSX.utils.book_new();

  for (const sheet of sheets) {
    const data = [sheet.headers, ...sheet.rows];
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Auto-size columns (rough heuristic)
    ws['!cols'] = sheet.headers.map((h, i) => {
      let maxLen = h.length;
      for (const row of sheet.rows) {
        const cellLen = String(row[i] ?? '').length;
        if (cellLen > maxLen) maxLen = cellLen;
      }
      return { wch: Math.min(maxLen + 2, 50) };
    });

    XLSX.utils.book_append_sheet(wb, ws, sheet.name.substring(0, 31));
  }

  XLSX.writeFile(wb, fileName);
}

/**
 * Generate and download a single-sheet CSV.
 */
export function downloadCSV(
  headers: string[],
  rows: (string | number | boolean | null | undefined)[][],
  fileName: string
): void {
  const escape = (v: unknown): string => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.map(escape).join(','),
    ...rows.map(r => r.map(escape).join(','))
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
